import { 
  Component, 
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  TemplateRef,
  forwardRef
} from '@angular/core';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StepConfig } from './components/wizard.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootprintManager_ShellService } from './FootprintManager.shell.service';
import { FootprintManager_OperationService } from './FootprintManager.operation.service';
import { FootprintManager_DatasourceService } from './FootprintManager.datasource.index';
import { FootprintManager_FlowService } from './FootprintManager.flow.index';
import { FootprintManager_ReportService } from './FootprintManager.report.index';
import { FootprintManager_LocalizationService } from './FootprintManager.localization.service';
import { FootprintManager_inventory_transformation_advanced_wizard_ComponentContextService } from './FootprintManager.inventory_transformation_advanced_wizard.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootprintManager.frontend.types'
import { $frontendTypes as $types} from './FootprintManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

import { FootprintManager_inventory_transformation_source_line_editorComponent } from './FootprintManager.inventory_transformation_source_line_editor.component';
import { FootprintManager_inventory_transformation_additional_options_formComponent } from './FootprintManager.inventory_transformation_additional_options_form.component';
import { FootprintManager_inventory_transformation_target_line_editorComponent } from './FootprintManager.inventory_transformation_target_line_editor.component';
import { FootprintManager_inventory_transformation_source_lines_gridComponent } from './FootprintManager.inventory_transformation_source_lines_grid.component';


@Component({
  standalone: true,
  imports: [
    SharedModule,

    forwardRef(() => FootprintManager_inventory_transformation_source_line_editorComponent),
    forwardRef(() => FootprintManager_inventory_transformation_additional_options_formComponent),
    forwardRef(() => FootprintManager_inventory_transformation_target_line_editorComponent),
    forwardRef(() => FootprintManager_inventory_transformation_source_lines_gridComponent),
  ],
  selector: 'FootprintManager-inventory_transformation_advanced_wizard',
  templateUrl: './FootprintManager.inventory_transformation_advanced_wizard.component.html'
})
export class FootprintManager_inventory_transformation_advanced_wizardComponent extends BaseComponent implements OnInit, OnChanges {
  _stepConfigs: StepConfig[];

  inParams: { project_id: number, warehouse_id: number, inventory_transformation_id: number, source_line_id?: number } = { project_id: null, warehouse_id: null, inventory_transformation_id: null, source_line_id: null };
  //#region Inputs
  @Input('project_id') set $inParams_project_id(v: number) {
    this.inParams.project_id = v;
  }
  get $inParams_project_id(): number {
    return this.inParams.project_id;
  }
  @Input('warehouse_id') set $inParams_warehouse_id(v: number) {
    this.inParams.warehouse_id = v;
  }
  get $inParams_warehouse_id(): number {
    return this.inParams.warehouse_id;
  }
  @Input('inventory_transformation_id') set $inParams_inventory_transformation_id(v: number) {
    this.inParams.inventory_transformation_id = v;
  }
  get $inParams_inventory_transformation_id(): number {
    return this.inParams.inventory_transformation_id;
  }
  @Input('source_line_id') set $inParams_source_line_id(v: number) {
    this.inParams.source_line_id = v;
  }
  get $inParams_source_line_id(): number {
    return this.inParams.source_line_id;
  }
  //#endregion Inputs

  //#region Outputs
  @Output() 
  $commandsTmpRef = new EventEmitter<TemplateRef<any>>();
  @Output()
  $finish = new EventEmitter();
  //#endregion

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { is_properties_initialized?: boolean, source_line_id?: number, isContentValidated?: boolean, isPropertiesValidated?: boolean, processQueue?: string[], warehouse_id?: number, target_line_id?: number } = { };
  //#endregion
  constructor(private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: FootprintManager_ShellService,
private $datasources: FootprintManager_DatasourceService,
private $flows: FootprintManager_FlowService,
private $reports: FootprintManager_ReportService,
private $localization: FootprintManager_LocalizationService,
private $operations: FootprintManager_OperationService,
private $logger: CleanupLoggerService,
private $context: FootprintManager_inventory_transformation_advanced_wizard_ComponentContextService,
) { 
    super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.project_id)) {
        this.$missingRequiredInParams.push('project_id');
      }
      if(isNil(this.inParams.warehouse_id)) {
        this.$missingRequiredInParams.push('warehouse_id');
      }
      if(isNil(this.inParams.inventory_transformation_id)) {
        this.$missingRequiredInParams.push('inventory_transformation_id');
      }
  }

  initialized = false;

  async $init() {
    this.title = 'Add inventory to transform';
  
    const $wizard = this;
    const $utils = this.$utils;

    await this.on_init();

    this.initialized = true;
  }

  steps: {
    step1?: {
      outParams?: { source_line_id?: number }
    }
    step2?: {
      outParams?: { additional_options?: { general: { keep_source_creation_date: boolean, convert_packaging_to_base: boolean }, serial_number_options: { copy_weights: boolean, copy_volume: boolean, copy_dimensions: boolean }, lot_number_options: { copy_udfs: boolean, copy_lot_number: boolean, copy_vendor_lot_number: boolean } } }
    }
    step4?: {
      outParams?: { target_line_id?: number }
    }
    step6?: {
      outParams?: {  }
    }
  } = { };

  stepsResultFunc(result: any) {
    this.steps = result;
  }
  
  getStepConfigs(): StepConfig[] {
    if (!this._stepConfigs) {
      const $wizard = this;
      const $utils = this.$utils;

      this._stepConfigs = [
      {
          id: 'step1',
          title: 'Original inventory',
          component: FootprintManager_inventory_transformation_source_line_editorComponent,
          inParamsFunc: ($index?: number) => {
            return {
              project_id: $wizard.inParams.project_id,
              warehouse_id: $wizard.inParams.warehouse_id,
              inventory_transformation_id: $wizard.inParams.inventory_transformation_id,
              source_line_id: $wizard.inParams.source_line_id,
            }
          },
          next: 'step2',
          nextButtonDisabledConditionFunc: ($index?: number) => {
            return !$utils.isDefined($wizard.vars.source_line_id) || $wizard.vars.source_line_id === 0;
          },
      },
      {
          id: 'step2',
          title: 'Additional options',
          component: FootprintManager_inventory_transformation_additional_options_formComponent,
          inParamsFunc: ($index?: number) => {
            return {
              inventory_transformation_id: $wizard.inParams.inventory_transformation_id,
              source_line_id: $wizard?.vars?.source_line_id,
            }
          },
          next: 'step4',
          nextButtonDisabledConditionFunc: ($index?: number) => {
            return !$utils.isDefined($wizard.vars.source_line_id) || !$utils.isDefined($wizard?.steps?.step2?.outParams?.additional_options);
          },
      },
      {
          id: 'step4',
          title: 'Inventory to transform',
          component: FootprintManager_inventory_transformation_target_line_editorComponent,
          inParamsFunc: ($index?: number) => {
            return {
              inventory_transformation_id: $wizard.inParams.inventory_transformation_id,
              source_line_id: $wizard.vars.source_line_id,
              projectId: $wizard.inParams.project_id,
              warehouse_id: $wizard.inParams.warehouse_id,
              additional_options: $wizard.steps.step2.outParams.additional_options,
            }
          },
          next: 'step6',
          nextButtonDisabledConditionFunc: ($index?: number) => {
            return !$utils.isDefined($wizard.vars.target_line_id);
          },
      },
      {
          id: 'step6',
          title: 'Summary',
          component: FootprintManager_inventory_transformation_source_lines_gridComponent,
          inParamsFunc: ($index?: number) => {
            return {
              inventory_transformation_id: $wizard.inParams.inventory_transformation_id,
              line_number: $wizard.vars.source_line_id,
              project_id: $wizard.inParams.project_id,
              warehouse_id: $wizard.inParams.warehouse_id,
              inventory_transformation_status_id: null,
              source_read_only: true,
              target_read_only: true,
            }
          },
          nextButtonLabel: 'Finish',
      },
      ];
    }

    return this._stepConfigs;
  }

  async finish() {
    const $wizard = this;
    const $utils = this.$utils;


    this.close();
  }

  close() {
    this.$finish.emit();
  }

  commandsTmpRefChange(tmp: any) {
    this.$commandsTmpRef.emit(tmp);
  }
 
  //#region private flows
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $wizard: FootprintManager_inventory_transformation_advanced_wizardComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_inventory_transformation_advanced_wizard_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
    this.$logger.log('FootprintManager', 'inventory_transformation_advanced_wizard.on_init');
  //run function to listen output from editor
  await add_events() 
  
  /************************************************
   * FUNCTIONS
  *************************************************/
  
  
  
  async function add_events() {
      await new Promise(result => setTimeout(result, 100));
  
      // Get element to attach to 
      const element = document?.getElementsByTagName('FootprintManager-inventory_transformation_advanced_wizard')[0]; ///change to FootprintManager-inventory_transformation_advanced_wizard
  
      if (element == null) {
          // throw new Error(`Failed to identify the HTML element for pack verification wizard`);
      }
  
      // 'on' events to detect different stages of the wizard
      element?.addEventListener('on_inventory_transformation_source_line_editor_initialized', ((event?: CustomEvent | null) => { }) as EventListener);
  
  
      // 'handle' events to perform common actions that may be invoked
      element?.addEventListener('handle_source_line_value', ((event?: CustomEvent<number[]> | null) => {
          $wizard.vars.source_line_id = event?.detail[0] ?? null
      }) as EventListener);
  
  
      // 'on' events to detect different stages of the wizard
      element?.addEventListener('on_inventory_transformation_target_line_editor_initialized', ((event?: CustomEvent | null) => { }) as EventListener);
  
  
      // 'handle' events to perform common actions that may be invoked
      element?.addEventListener('handle_target_line_value', ((event?: CustomEvent<number> | null) => {
          $wizard.vars.target_line_id = event?.detail ?? null
      }) as EventListener);
  
  
  }
  
  
  }
  //#endregion private flows
}
